/**
 * SpeedCoachModule — crew-facing LIVE drive-through coaching (QSR add-on).
 * Simulates a live peak window: a ticking order timer, per-station pacing,
 * per-store status and rotating nudges pushed to crew. Feels alive via timers.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Radio, Timer, Zap, TrendingUp, TrendingDown, BellRing, AlertTriangle } from 'lucide-react';
import { QSR_STORES, TARGETS } from './qsrDemoData';
import AskCorvana from './AskCorvana';

const NUDGES = [
  'Greet the car within 3 seconds — eye contact on the screen.',
  'Confirm the combo upsell before the total.',
  'Call the order to the line early — don’t wait for pay.',
  'Bag drinks first, hot food last, check the seal.',
  'Present with the name + a thank-you — keep the lane moving.',
  'Two cars stacked — pre-pull the next ticket now.',
];
const STATIONS = ['Order', 'Pay', 'Present'];

export default function SpeedCoachModule() {
  const [elapsed, setElapsed] = useState(0); // seconds on the live car
  const [nudge, setNudge] = useState(0);
  const [tick, setTick] = useState(0);
  const [threshold, setThreshold] = useState(3); // cars over target before pinging the manager
  const [alerts, setAlerts] = useState([]);
  const streaks = useRef({});
  const target = TARGETS.sos * 60; // seconds
  const raf = useRef();

  // Coach Alerts: ping the manager when a store stays over target for > threshold cars
  useEffect(() => {
    streaks.current = {};
    let c = 0;
    const id = setInterval(() => {
      c += 1;
      QSR_STORES.forEach((s, idx) => {
        const pace = s.sos + Math.sin((c + idx * 7) / 2.2) * 0.25;
        if (pace > TARGETS.sos) {
          streaks.current[s.id] = (streaks.current[s.id] || 0) + 1;
          if (streaks.current[s.id] >= threshold) {
            streaks.current[s.id] = 0;
            setAlerts((prev) => [{ id: `${s.id}_${Date.now()}`, store: s.name, manager: s.manager, cars: threshold, pace: pace.toFixed(1), at: new Date() }, ...prev].slice(0, 5));
          }
        } else {
          streaks.current[s.id] = 0;
        }
      });
    }, 2500);
    return () => clearInterval(id);
  }, [threshold]);

  useEffect(() => {
    const id = setInterval(() => {
      setElapsed((e) => (e > target + 55 ? 8 : e + 1));
      setTick((t) => t + 1);
    }, 1000);
    return () => clearInterval(id);
  }, [target]);

  useEffect(() => {
    const id = setInterval(() => setNudge((n) => (n + 1) % NUDGES.length), 4200);
    return () => clearInterval(id);
  }, []);

  const onPace = elapsed <= target;
  const mm = Math.floor(elapsed / 60);
  const ss = String(elapsed % 60).padStart(2, '0');
  const pct = Math.min(100, (elapsed / (target * 1.4)) * 100);

  // live per-store pace oscillates gently around each store's avg
  const jitter = (base) => base + Math.sin((tick + base * 10) / 3) * 0.25;

  return (
    <div className="space-y-4 qsr-stage p-3 sm:p-5" data-testid="speedcoach-module">
      {/* Live coaching hero */}
      <div className="rounded-3xl bg-gradient-to-br from-slate-900 to-slate-700 text-white p-5 sm:p-6">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 text-rose-300 text-xs font-bold uppercase tracking-wider">
            <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-500 opacity-75" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-500" /></span>
            <Radio className="w-4 h-4" /> Live · Dinner peak
          </div>
          <span className="text-xs text-slate-300">Target speed of service {TARGETS.sos.toFixed(1)}m</span>
        </div>
        <div className="mt-4 flex items-end gap-4 flex-wrap">
          <div>
            <div className="text-xs text-slate-300 mb-1">Car on window</div>
            <div className={`text-5xl font-semibold tabular-nums tracking-tight ${onPace ? 'text-emerald-300' : 'text-rose-300'}`} data-testid="speedcoach-timer">{mm}:{ss}</div>
          </div>
          <div className={`mb-1.5 inline-flex items-center gap-1 text-sm font-semibold ${onPace ? 'text-emerald-300' : 'text-rose-300'}`}>
            {onPace ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
            {onPace ? 'On pace' : 'Push — over target'}
          </div>
        </div>
        <div className="mt-3 h-2.5 rounded-full bg-white/15 overflow-hidden">
          <div className={`h-full rounded-full transition-all duration-500 ${onPace ? 'bg-emerald-400' : 'bg-rose-400'}`} style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* Rotating crew nudge */}
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-center gap-3" data-testid="speedcoach-nudge">
        <div className="p-2 rounded-xl bg-amber-100 text-amber-600 shrink-0"><Zap className="w-4 h-4" /></div>
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">Nudge to crew</div>
          <div key={nudge} className="text-sm font-medium text-slate-800 animate-in fade-in slide-in-from-bottom-1 duration-500">{NUDGES[nudge]}</div>
        </div>
      </div>

      {/* Station pacing */}
      <div className="grid grid-cols-3 gap-3">
        {STATIONS.map((st, i) => {
          const t = [Math.max(2, 6 - i * 1 + Math.round(Math.sin(tick / 2 + i) * 2)), 0][0];
          const good = t <= [8, 25, 15][i];
          return (
            <div key={st} className="rounded-2xl qsr-card p-3.5 shadow-sm" data-testid={`speedcoach-station-${st.toLowerCase()}`}>
              <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium"><Timer className="w-3.5 h-3.5" />{st}</div>
              <div className={`text-2xl font-semibold tabular-nums mt-1 ${good ? 'text-slate-900' : 'text-rose-600'}`}>{[t + 4, t + 20, t + 9][i]}s</div>
              <div className={`text-[11px] font-medium mt-0.5 ${good ? 'text-emerald-600' : 'text-amber-600'}`}>{good ? 'On pace' : 'Watch'}</div>
            </div>
          );
        })}
      </div>

      {/* Live store board */}
      <div className="rounded-2xl qsr-card shadow-sm divide-y divide-slate-100" data-testid="speedcoach-board">
        <div className="px-4 py-2.5 text-sm font-semibold text-slate-900">Live store board</div>
        {QSR_STORES.map((s) => {
          const live = jitter(s.sos);
          const good = live <= TARGETS.sos;
          return (
            <div key={s.id} className="flex items-center gap-3 px-4 py-3">
              <span className={`h-2 w-2 rounded-full ${good ? 'bg-emerald-500' : 'bg-rose-500 animate-pulse'}`} />
              <span className="font-semibold text-sm text-slate-900 flex-1">{s.name}</span>
              <span className="text-xs text-slate-500">now</span>
              <span className={`text-sm font-semibold tabular-nums w-16 text-right ${good ? 'text-emerald-600' : 'text-rose-600'}`}>{live.toFixed(1)}m</span>
              <span className={`text-[11px] font-medium w-16 text-right ${good ? 'text-emerald-600' : 'text-amber-600'}`}>{good ? 'On pace' : 'Coaching'}</span>
            </div>
          );
        })}
      </div>

      {/* Coach alerts — auto-ping the manager when a store slips off pace */}
      <div className="qsr-card rounded-2xl p-4" data-testid="coach-alerts">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="p-1.5 rounded-lg bg-rose-50 text-rose-600"><BellRing className="w-4 h-4" /></span>
            <div>
              <div className="text-sm font-semibold text-slate-900">Coach alerts</div>
              <div className="text-[11px] text-slate-500">Auto-pings the store manager when a store runs over target</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <AskCorvana insight="Coach alerts fire when a store runs over target for the set number of cars. Penrith and Southport trip most at dinner — pre-position a runner before the 5pm rush instead of reacting to alerts." />
            <span className="text-[11px] text-slate-500 font-medium">Alert after</span>
            {[3, 5, 8].map((n) => (
              <button
                key={n}
                onClick={() => setThreshold(n)}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${threshold === n ? 'bg-slate-900 text-white border-slate-900' : 'bg-white/70 text-slate-600 border-slate-200 hover:border-slate-300'}`}
                data-testid={`coach-threshold-${n}`}
              >{n} cars</button>
            ))}
          </div>
        </div>
        <div className="mt-3 space-y-1.5">
          {alerts.length === 0 ? (
            <div className="text-xs text-slate-400 py-2">Monitoring live pace — no store has breached {threshold} cars over target yet.</div>
          ) : alerts.map((al) => (
            <div key={al.id} className="flex items-center gap-2.5 rounded-xl border border-rose-100 bg-rose-50/60 px-3 py-2 animate-in fade-in slide-in-from-top-1 duration-500" data-testid="coach-alert-row">
              <AlertTriangle className="w-4 h-4 text-rose-500 shrink-0" />
              <span className="text-sm text-slate-800 flex-1 min-w-0"><span className="font-semibold">{al.store}</span> over target for {al.cars} cars ({al.pace}m)</span>
              <span className="text-[11px] font-semibold text-rose-600 shrink-0">Pinged {al.manager}</span>
              <span className="text-[11px] text-slate-400 shrink-0 tabular-nums">{al.at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
          ))}
        </div>
        <div className="mt-2 text-[11px] text-slate-400">Demo — connect push/SMS to alert managers on their phone.</div>
      </div>
    </div>
  );
}
